import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { tokens } from "../../../../public/tokens";

const popoverEnter = keyframes({
    from: { opacity: 0, transform: "translateY(-4px)" },
    to: { opacity: 1, transform: "translateY(0)" },
});

/*
 * Every $-prop-dependent property here is its own per-property function, rather
 * than one top-level props function returning the whole object, for the same
 * reason documented in FloatingActionBar.js/Modal.js: `animation` wraps a
 * keyframes() result in css``, which only flattens correctly when the
 * surrounding object is a plain top-level object, not one returned from a
 * props function (that threw "interpolating a keyframe declaration into an
 * untagged string" here).
 */
export const StyledModulesPopover = styled.div({
    position: "fixed",

    top: ({ $top }) => `${$top}px`,
    left: ({ $left }) => `${$left}px`,

    width: ({ $expanded }) => ($expanded ? "361px" : "416px"),
    maxWidth: "calc(100vw - 24px)",
    /*
     * maxHeight must account for the popover's own top offset — it can be
     * positioned anywhere down the viewport (next to whichever row's
     * "Modules" button was clicked), so a flat "100vh - 24px" cap still let
     * content run past the bottom edge whenever top > 0.
     */
    maxHeight: ({ $top }) => `calc(100vh - ${$top}px - 12px)`,

    display: "flex",
    flexDirection: "column",
    overflow: "hidden",

    zIndex: 2100,

    background: tokens.colors.neutral.white,

    border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],

    boxShadow:
        tokens.effects.effects.shadow.drawer ||
        tokens.effects.effects.shadow.default,

    fontFamily:
        tokens.typography.fontFamilies["albert-sans"],

    animation: css`${popoverEnter} 150ms ease-out`,
    transition: "width 0.15s ease-in-out",
});

/*
 * Scrollable middle section (problems + Ready Modules) between the fixed
 * header and footer, so a long problem/ready list scrolls in place instead
 * of pushing the footer/header out of the viewport.
 */
export const StyledPopoverContent = styled.div({
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
});

export const StyledPopoverHeader = styled.div({
    width: "100%",
    flexShrink: 0,

    padding: tokens.globals["spacing-2"],

    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]}`,

    background: tokens.colors.neutral.white,

    boxSizing: "border-box",
});

export const StyledPopoverHeaderTop = styled.div({
    width: "100%",

    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",

    gap: tokens.globals["spacing-2"],
});

/*
 * Programme name + identity wrapper are shared with LinkedProgrammesTable's row via
 * linkedProgrammes.styles.js. The popover caps the name at 320px via $maxWidth,
 * passed explicitly at the call site in ModuleReadinessPopover.js.
 */
export {
    StyledProgrammeDetails as StyledPopoverProgramme,
    StyledProgrammeName as StyledPopoverProgrammeName,
} from "./linkedProgrammes.styles";

export const StyledPopoverClose = styled.button({
    width: "20px",
    height: "20px",

    padding: 0,
    border: 0,

    background: "transparent",
    color: tokens.colors.neutral.grey["700"],

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    flex: "0 0 20px",

    cursor: "pointer",

    i: {
        fontSize: "14px",
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },
});

/*
 * Meta row + method badge are shared with LinkedProgrammesTable's row via
 * linkedProgrammes.styles.js — both render the same campus/method cluster.
 */
export {
    StyledProgrammeMetaRow as StyledPopoverMeta,
    StyledMethod as StyledPopoverMethod,
} from "./linkedProgrammes.styles";

export const StyledPopoverDivider = styled.span({
    width: "1px",
    height: "18px",

    flex: "0 0 1px",

    background: tokens.colors.surfaces["border-divider"],
});

export const StyledPopoverSmallText = styled.span({
    color: tokens.colors.text.primary,

    fontSize: tokens.typography.fontSize["sm-10px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "18px",

    whiteSpace: "nowrap",
});

export const StyledPopoverVersion = styled.span({
    display: "inline-flex",
    alignItems: "center",

    gap: tokens.globals["spacing-1"],

    color: tokens.colors.brand.blue["500"],

    fontSize: tokens.typography.fontSize["sm-10px"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: "18px",

    whiteSpace: "nowrap",

    i: {
        fontSize: "10px",
    },
});

export const StyledOverrideNotice = styled.div({
    width: "100%",

    marginTop: tokens.globals["spacing-1-5"],
    padding: tokens.globals["spacing-2"],

    display: "flex",
    alignItems: "flex-start",

    gap: tokens.globals["spacing-1-5"],

    borderRadius: tokens.globals["border-radius"],

    background: tokens.colors.neutral.grey["100"],

    boxSizing: "border-box",

    i: {
        flex: "0 0 8px",

        marginTop: "1px",

        color: tokens.colors.neutral.grey["600"],

        fontSize: "8px",
        lineHeight: "8px",
    },

    span: {
        color: tokens.colors.text.primary,

        fontSize: tokens.typography.fontSize["sm-10px"],
        fontWeight: tokens.typography.fontWeights.regular,
        lineHeight: 1.1,
    },
});

export const StyledPopoverIssues = styled.div({
    width: "100%",

    padding: tokens.globals["spacing-2"],

    display: "flex",
    flexDirection: "column",

    gap: tokens.globals["spacing-0-5"],

    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]}`,

    background: tokens.colors.neutral.white,

    boxSizing: "border-box",
});

export const StyledIssueRow = styled.div({
    width: "100%",

    padding: `${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-1"]}`,

    display: "flex",
    alignItems: "flex-start",

    gap: tokens.globals["spacing-2"],

    boxSizing: "border-box",
});

export const StyledIssueIcon = styled.i(({ $tone }) => {
    const tones = {
        warning: tokens.colors.states.warning["500"],
        destructive: tokens.colors.ui.destructive,
        muted: tokens.colors.neutral.grey["600"],
        info: tokens.colors.brand["plum-orchid"]["500"],
    };

    return {
        width: "14px",
        height: "14px",

        flex: "0 0 14px",

        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",

        color: tones[$tone] || tones.muted,

        fontSize: "14px",
        lineHeight: "14px",
    };
});

export const StyledIssueContent = styled.div({
    minWidth: 0,

    flex: 1,

    display: "flex",
    flexDirection: "column",

    gap: tokens.globals["spacing-1"],
});

export const StyledIssueMain = styled.div({
    minWidth: 0,

    display: "flex",
    alignItems: "center",

    gap: tokens.globals["spacing-1-5"],

    flexWrap: "wrap",
});

export const StyledIssueModuleName = styled.span({
    color: tokens.colors.text.primary,

    fontSize: "11px",
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "normal",

    whiteSpace: "nowrap",
});

export const StyledIssueDivider = styled.span({
    width: "1px",
    height: "12px",

    background: tokens.colors.surfaces["border-divider"],
});

export const StyledIssueCode = styled.span(
    ({ $destructive }) => ({
        color: $destructive
            ? tokens.colors.ui.destructive
            : tokens.colors.text.link,

        fontFamily:
            tokens.typography.fontFamilies["albert-sans"],
        fontSize: "11px",
        fontWeight:
            tokens.typography.fontWeights.semibold,
        lineHeight: "normal",

        whiteSpace: "nowrap",
    })
);

export const StyledIssueArrow = styled.span({
    color: tokens.colors.neutral.grey["500"],

    fontSize: "10px",
    fontWeight: tokens.typography.fontWeights.bold,

    whiteSpace: "nowrap",
});

export const StyledIssueDescription = styled.span({
    maxWidth: "310px",

    color: tokens.colors.neutral.grey["700"],

    fontSize: "8.5px",
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "normal",
});

export const StyledMismatchTags = styled.div({
    display: "flex",
    alignItems: "center",

    gap: tokens.globals["spacing-1"],

    flexWrap: "wrap",
});

export const StyledMismatchTag = styled.span({
    minHeight: "18px",

    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,

    borderRadius: "10px",

    background: tokens.colors.brand.naartjie["100"],
    color: tokens.colors.brand.naartjie["800"],

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    gap: tokens.globals["spacing-1"],

    fontSize: tokens.typography.fontSize["sm-10px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: 1,

    whiteSpace: "nowrap",

    i: {
        fontSize: "8px",
    },
});

export const StyledReadyHeader = styled.button(
    ({ $expanded }) => ({
        width: "100%",

        padding: "8px",

        border: 0,
        borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]}`,

        background: $expanded
            ? tokens.colors.neutral.grey["100"]
            : tokens.colors.neutral.white,

        display: "flex",
        alignItems: "center",

        gap: `${tokens.globals["spacing-1-5"]}`,

        cursor: "pointer",

        fontFamily:
            tokens.typography.fontFamilies["albert-sans"],

        "&:focus-visible": {
            outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
            outlineOffset: "-2px",
        },
    })
);

export const StyledReadyHeaderTitle = styled.span({
    display: "inline-flex",
    alignItems: "center",

    gap: tokens.globals["spacing-1"],

    color: tokens.colors.text.primary,

    fontSize: tokens.typography.fontSize["md-12px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "16px",

    whiteSpace: "nowrap",

    i: {
        width: tokens.globals["spacing-4"],
        fontSize: "12px",
    },
});

export const StyledReadyHeaderSpacer = styled.span({
    flex: 1,
});

export const StyledReadyStateText = styled.span({
    color: tokens.colors.neutral.grey["500"],

    fontSize: "9px",
    fontWeight: tokens.typography.fontWeights.regular,

    whiteSpace: "nowrap",
});

export const StyledReadyBody = styled.div({
    width: "100%",

    padding: tokens.globals["spacing-2"],

    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]}`,

    background: tokens.colors.neutral.white,

    boxSizing: "border-box",

    maxHeight: "240px",
    overflowY: "auto",
});

export const StyledReadyList = styled.div({
    display: "flex",
    flexDirection: "column",

    gap: tokens.globals["spacing-0-5"],
});

export const StyledReadyItem = styled.div({
    width: "100%",

    padding: `${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-1"]}`,

    display: "flex",
    alignItems: "center",

    gap: tokens.globals["spacing-2"],

    boxSizing: "border-box",
});

export const StyledReadyCheck = styled.span({
    width: "16px",
    height: "16px",

    flex: "0 0 16px",

    borderRadius: "3px",

    background: tokens.colors.states.success["500"],
    color: tokens.colors.neutral.white,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    fontSize: "10px",
    fontWeight: tokens.typography.fontWeights.bold,
});

export const StyledReadyName = styled.span({
    color: tokens.colors.text.primary,

    fontSize: "10.5px",
    fontWeight: tokens.typography.fontWeights.semibold,

    whiteSpace: "nowrap",
});

export const StyledReadyArrow = styled.span({
    color: tokens.colors.neutral.grey["500"],

    fontSize: "9px",
    fontWeight: tokens.typography.fontWeights.bold,
});

export const StyledReadyCode = styled.span({
    color: tokens.colors.text.link,

    fontSize: "10.5px",
    fontWeight: tokens.typography.fontWeights.semibold,

    whiteSpace: "nowrap",
});

export const StyledPopoverFooter = styled.div({
    width: "100%",
    flexShrink: 0,

    padding: tokens.globals["spacing-2"],

    display: "flex",
    alignItems: "center",

    gap: tokens.globals["spacing-2"],

    background: tokens.colors.neutral.grey["100"],

    boxSizing: "border-box",
});

export const StyledErrorSummary = styled.span({
    color: tokens.colors.neutral.grey["700"],

    fontSize: "10.5px",
    fontWeight: tokens.typography.fontWeights.regular,

    whiteSpace: "nowrap",
});

export const StyledPopoverFooterSpacer = styled.span({
    flex: 1,
});

export const StyledOpenModulesLink = styled(Link)({
    color: tokens.colors.text.link,

    fontSize: "11px",
    fontWeight: tokens.typography.fontWeights.semibold,

    textDecoration: "none",

    whiteSpace: "nowrap",

    "&:hover": {
        textDecoration: "underline",
    },
});
